import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Select } from 'src/components/Formik'
import * as PoliciesService from 'src/services/api/policies'
import { Id, PolicyRule } from 'src/types'

import { SelectProps, Option } from '../Select'
import { SelectFake } from '../select-fake'

type PolicyRules = Pick<PolicyRule, 'policy_rule_id' | 'name'>

interface GetPolicyRulesResponse {
  policy_rules: [PolicyRules]
}

interface Props extends Omit<SelectProps, 'name' | 'options'> {
  name?: string
}

function manageDataToOption(data: GetPolicyRulesResponse): Option[] {
  if (!data?.policy_rules) {
    return []
  }

  return data.policy_rules.map(d => ({
    label: `${d.name}`,
    title: `${d.name}`,
    value: `${d.policy_rule_id}`
  }))
}

export default function SelectPolicyRule({
  formikProps,
  required,
  ...props
}: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [hasError, setHasError] = useState<string>('')
  const [options, setOptions] = useState<Option[]>([])
  const [organizationId, setOrganizationId] = useState<Id>(
    formikProps.values?.organization_id
  )
  const { t } = useTranslation()

  useEffect(() => {
    if (formikProps.values?.organization_id !== organizationId) {
      setOrganizationId(formikProps.values.organization_id)
    }
  }, [formikProps, organizationId])

  useEffect(() => {
    let isSubscribed = true
    if (organizationId) {
      setIsLoading(true)
      setHasError('')
      PoliciesService.getPolicyRules({ organization_id: organizationId })
        .then(response => {
          if (isSubscribed && response?.status === 200) {
            const defaultOptions = manageDataToOption(response?.data)
            if (formikProps.values?.policy_rule_id) {
              const record = response.data.policy_rules.find(
                rule =>
                  `${rule.policy_rule_id}` ===
                  `${formikProps.values?.policy_rule_id}`
              )
              if (!record) {
                formikProps.setFieldValue(
                  'policy_rule_id',
                  defaultOptions?.[0]?.value || ''
                )
              }
            }

            setOptions(defaultOptions)
            return
          }
          setHasError(t('common.response.unexpected-error'))
        })
        .catch(() => {
          if (isSubscribed) {
            setHasError(t('common.response.unexpected-error'))
          }
        })
        .finally(() => {
          if (isSubscribed) {
            setIsLoading(false)
          }
        })
    }
    return () => {
      isSubscribed = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId])

  const label = 'web.anti-fraud.policy-rule'
  const name = 'policy_rule_id'

  if (hasError) {
    const fakeOptions = {
      label: `Loading error`
    }

    return (
      <SelectFake
        fakeOptions={fakeOptions}
        formikProps={formikProps}
        label={label}
        name={name}
        required={required}
      />
    )
  }

  if (isLoading) {
    const fakeOptions = {
      label: ''
    }

    return (
      <SelectFake
        fakeOptions={fakeOptions}
        label={label}
        loading
        required={required}
      />
    )
  }

  return (
    <Select
      {...props}
      dataTestId="policy-rule-list"
      formikProps={formikProps}
      label={label}
      name={name}
      options={options}
      required={required}
    />
  )
}
